import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "wall",
    "image",
    "material",
    "linesForm",
    "colorForm",
    "popup",
    "editLink",
    "deleteLink",
    "thicknessInput",
    "warnDrawPopup",
    "linesCount",
    "wallScale",
  ];

  static values = {
    projectId: String,
    floorPlanId: String,
    walls: Array || [],
  };

  connect() {
    if (!this.hasImageTarget || !this.hasWallTarget) return;
    this.scale = 1;
    this.enabled = true;
    this.isShow = false;
    this.startX = null;
    this.startY = null;
    this.endX = null;
    this.endY = null;
    this.color = "#000000";
    this.lines = [];
    this.isDrawingFirstLine = false;
    this.hasPendingStartPoint = false;
    this.mode = "browse"; // default mode
    this.selectedWall = null;
    this.dragThreshold = 2;     // (px)
    this.longPressDelay = 200;  // (ms)
    this.isPressing = false;
    this.isDragging = false;
    this.longPressTimer = null;
    this.dragStart = null;

    // mode: "browse" | "draw"
    this.mode = "browse";

    // Detect if we're in edit mode by checking the URL
    this.isEditMode = this.detectEditMode();
    // Store the original wall data for edit mode
    if (this.isEditMode) {
      this.originalWallData = this.extractOriginalWallData();
    }

    const onImageReady = () => {
      this.resizeCanvas();
      if (this.hasColorFormTarget) {
        this.color = this.colorFormTarget?.value;
      }
      this.wall = this.wallTarget;
      this.image = this.imageTarget;
      this.ctx = this.wall.getContext("2d");

      if (this.wallsValue && this.wallsValue.length > 0) {
        this.drawAllLines();
      }
    };

    // binding mousedown / mouseup / mousemove
    this.wallTarget.addEventListener(
      "mousedown",
      this.handleMouseDown.bind(this),
    );
    this.wallTarget.addEventListener(
      "mousemove",
      this.handleMouseMove.bind(this),
    );
    this.wallTarget.addEventListener("mouseup", this.handleMouseUp.bind(this));

    // keyboard
    document.addEventListener("keydown", this.handleKeyDown.bind(this));

    this.boundBeforeUnloadHandler = this.handleBeforeUnload.bind(this);
    window.addEventListener("beforeunload", this.boundBeforeUnloadHandler);

    if (this.imageTarget.complete) {
      onImageReady();
    } else {
      this.imageTarget.addEventListener("load", onImageReady);
    }
  }

  disconnect() {
    if (this.wall) {
      this.wall.removeEventListener("mousedown", this.handleMouseDown);
      this.wall.removeEventListener("mouseup", this.handleMouseUp);
      this.wall.removeEventListener("mousemove", this.handleMouseMove);
      this.wall.removeEventListener("mouseleave", this.handleMouseLeave);
    }
    document.removeEventListener("keydown", this.handleKeyDown);
    if (this.boundBeforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.boundBeforeUnloadHandler);
    }
  }

  setMode(event) {
    const previousMode = this.mode;
    const mode = event.params.mode;

    if (previousMode === "draw" && mode === "browse") {
      this.clearUnfinishedWall();
    }

    this.mode = mode;

    const cursorIcon = document.getElementById("cursor-action");
    const drawIcon = document.getElementById("draw-wall-action");

    // reset toolbar active style
    cursorIcon.classList.remove("active", "icon-green");
    drawIcon.classList.remove("active", "icon-green");

    if (mode === "draw") {
      drawIcon.classList.add("active", "icon-green");
      this.wallTarget.style.cursor = "crosshair";
    } else {
      cursorIcon.classList.add("active", "icon-green");
      this.wallTarget.style.cursor = "default";
    }
  }

  clearUnfinishedWall() {
    const hasUnfinishedWall =
      this.startX !== null &&
      this.startY !== null &&
      (this.endX === null || this.endY === null);

    if (!hasUnfinishedWall && !this.hasPendingStartPoint) return;

    this.startX = null;
    this.startY = null;
    this.endX = null;
    this.endY = null;
    this.isDrawingFirstLine = false;
    this.hasPendingStartPoint = false;
    this.redrawAll();
  }

  updateCursor(style) {
    if (this.wallTarget) {
      this.wallTarget.style.cursor = style;
    }
  }

  // =========================
  // Mouse Handlers
  // =========================

  handleMouseDown(e) {
    if (e.button !== 0) return;
    
    // Ignore if clicking inside control panel area
    if (e.target.closest(".management") || 
        e.target.closest("#floorplan_body") ||
        e.target.closest(".management__contain") ||
        e.target.closest(".management__toolbar") ||
        e.target.closest("#management_section > .management")) {
      return;
    }
    
    // Ignore if interacting with input elements
    if (e.target.closest("input[type='range']") || 
        e.target.closest("#power_percentage_slider") ||
        e.target.closest(".power-slider-container") ||
        e.target.tagName === "INPUT" ||
        e.target.tagName === "BUTTON" ||
        e.target.tagName === "SELECT") {
      return;
    }

    this.isPressing = true;
    this.isDragging = false;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;

    // long press 200ms to start dragging
    this.longPressTimer = setTimeout(() => {
      if (this.isPressing && !this.isDragging) {
        this.isDragging = true;
        this.updateCursor("grabbing");
        this.element.dispatchEvent(
          new CustomEvent("zoom:startDrag", { detail: { event: e }, bubbles: true })
        );
      }
    }, this.longPressDelay);
  }

  handleMouseMove(e) {
    if (!this.isPressing) return;
    
    // Stop if mouse moves into control panel area
    if (e.target.closest(".management") || 
        e.target.closest("#floorplan_body") ||
        e.target.closest(".management__contain") ||
        e.target.closest(".management__toolbar") ||
        e.target.closest("#management_section > .management")) {
      this.handleMouseUp(e);
      return;
    }
    
    // Stop if interacting with input elements
    if (e.target.closest("input[type='range']") || 
        e.target.closest("#power_percentage_slider") ||
        e.target.closest(".power-slider-container") ||
        e.target.tagName === "INPUT" ||
        e.target.tagName === "BUTTON" ||
        e.target.tagName === "SELECT") {
      this.handleMouseUp(e);
      return;
    }

    const dx = e.clientX - this.dragStartX;
    const dy = e.clientY - this.dragStartY;

    // if over 5px ->  dragging
    if (!this.isDragging && Math.sqrt(dx * dx + dy * dy) > this.dragThreshold) {
      clearTimeout(this.longPressTimer);
      this.isDragging = true;
      this.updateCursor("grabbing");
      this.element.dispatchEvent(
        new CustomEvent("zoom:startDrag", { detail: { event: e }, bubbles: true })
      );
    }

    if (this.isDragging) {
      this.element.dispatchEvent(
        new CustomEvent("zoom:drag", { detail: { event: e }, bubbles: true })
      );
    }
  }

  handleMouseUp(e) {
    clearTimeout(this.longPressTimer);

    const rect = this.wallTarget.getBoundingClientRect();
    const x = (typeof e.offsetX === "number") ? e.offsetX : (e.clientX - rect.left);
    const y = (typeof e.offsetY === "number") ? e.offsetY : (e.clientY - rect.top);

    if (this.isDragging) {
      this.element.dispatchEvent(
        new CustomEvent("zoom:stopDrag", { detail: { event: e }, bubbles: true })
      );
      this.updateCursor(this.mode === "draw" ? "crosshair" : "default");
    } else {
      if (this.mode === "draw") {
        this.handleDrawClick(x, y);
      } else {
        this.handleBrowseClick(x, y);
      }
    }

    this.isPressing = false;
    this.isDragging = false;
  }

  addLine(x1, y1, x2, y2, color) {
    const newLine = {
      startX: Math.round(x1),
      startY: Math.round(y1),
      endX: Math.round(x2),
      endY: Math.round(y2),
      color
    };

    if (this.isEditMode && this.lines.length === 0 && this.originalWallData) {
      // replace the original wall being edited
      this.lines.push(newLine);
      this.wallsValue = this.wallsValue.map(wall =>
        wall.id.toString() === this.originalWallData.id
          ? { ...newLine, id: wall.id, material: wall.material, thickness: wall.thickness, unit: wall.unit }
          : wall
      );
    } else {
      // add new wall line
      this.lines.push(newLine);
      this.wallsValue.push(newLine);
    }

    this.setFormFields(JSON.stringify(this.lines));
    this.updateLinesInfo();
    this.redrawAll();
  }


  handleBrowseClick(x, y) {
    const allWalls = [...this.wallsValue, ...this.lines];
    const clickedWall = allWalls.find((wall) => this.isNearLine(x, y, wall));

    if (clickedWall) {
      this.selectedWall = clickedWall;
      this.redrawAll();
      this.drawLine(
        clickedWall.startX,
        clickedWall.startY,
        clickedWall.endX,
        clickedWall.endY,
        "#3c9f00",
        1.0,
      );
    } else {
      this.selectedWall = null;
      this.redrawAll();
    }
  }

  handleDrawClick(x, y) {
    if (this.startX == null) {
      this.startX = x;
      this.startY = y;
      this.drawStartPoint(x, y);
      this.hasPendingStartPoint = true;
    } else {
      const dx = x - this.startX;
      const dy = y - this.startY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance >= 10) {
        this.addLine(this.startX, this.startY, x, y, this.color);
        this.hasPendingStartPoint = false;
      } else if (this.hasPendingStartPoint) {
        // Remove the stray start point when a valid wall was not created
        this.hasPendingStartPoint = false;
        this.redrawAll();
      }

      // reset
      this.startX = this.startY = null;
      this.endX = this.endY = null;
    }
  }

  deleteSelectedWall(event) {
    event.preventDefault();
    if (!this.selectedWall) {
      this.showToast("No wall selected to delete.", "warning");
      return;
    }

    const wall = this.selectedWall;

    // ✅ 分支1: 若是資料庫既有牆
    if (wall.id) {
      // 加上標記，代表這筆牆要刪除
      wall.isDeleted = true;

      // 可選：立即在畫面上隱藏它（但不從陣列移除）
      wall.hidden = true;

      // 更新 lines 陣列（替換同 id 的牆）
      this.lines = this.lines.map((w) => (w.id === wall.id ? wall : w));

      // 前端 UI 更新
      this.redrawAll();
      this.hidePopup();
      this.setFormFields(JSON.stringify(this.lines));
      this.showToast("Wall marked for deletion", "info");
    }
    // ✅ 分支2: 若是前端新建的牆（尚無ID）
    else {
      this.wallsValue = this.wallsValue.filter((w) => w !== wall);
      this.lines = this.lines.filter((w) => w !== wall);
      this.redrawAll();
      this.hidePopup();
      this.setFormFields(JSON.stringify(this.lines));
      this.showToast("Wall removed (not saved yet)", "success");
    }

    this.selectedWall = null;
  }

  resizeCanvas() {
    const canvas = this.wallTarget;
    const img = this.imageTarget;

    const width = img.clientWidth || img.naturalWidth;
    const height = img.clientHeight || img.naturalHeight;

    canvas.width = width;
    canvas.height = height;

    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
  }

  detectEditMode() {
    const currentPath = window.location.pathname;
    return (
      currentPath.includes("/edit") || currentPath.match(/\/walls\//d + /edit$/)
    );
  }

  extractOriginalWallData() {
    // Extract the original wall data from the form or URL
    // This will be used to identify which wall to remove from canvas when drawing starts
    const currentPath = window.location.pathname;
    const wallIdMatch = currentPath.match(/\/walls\/(\d+)\/edit$/);
    if (wallIdMatch) {
      return {
        id: wallIdMatch[1],
        isOriginal: true,
      };
    }
    return null;
  }

  showEditModeWarning() {
    // Check if warning already exists, if so just show it
    const existingWarning = document.getElementById("edit_mode_warning");
    if (existingWarning) {
      existingWarning.style.display = "block";
      return;
    }

    // Create and show a warning message for edit mode
    const warningDiv = document.createElement("div");
    warningDiv.id = "edit_mode_warning";
    warningDiv.className = "floor-plan__form__warning";
    warningDiv.style.display = "block";
    warningDiv.innerHTML = `
      <span class="text-f1 warning-text">
        ⚠️ <strong>Edit Mode:</strong> You can edit wall properties without drawing. 
        If you draw lines: the first line will update the original wall position, additional lines will create new walls with the same material and thickness.
      </span>
    `;

    // Insert the warning after the lines-info div
    const linesInfo = document.querySelector(".floor-plan__form__lines-info");
    if (linesInfo && linesInfo.parentNode) {
      linesInfo.parentNode.insertBefore(warningDiv, linesInfo.nextSibling);
    }
  }

  hideEditModeWarning() {
    const warningDiv = document.getElementById("edit_mode_warning");
    if (warningDiv) {
      warningDiv.style.display = "none";
    }
  }

  drawStartPoint(x, y) {
    // Draw a small circle at the start point to indicate where drawing started
    this.ctx.beginPath();
    this.ctx.arc(x, y, 3, 0, 2 * Math.PI);
    this.ctx.fillStyle = this.color;
    this.ctx.fill();
  }

  cancelDraw() {
    if (this.isDrawing) {
      this.isDrawing = false;
      this.startX = null;
      this.startY = null;
      this.endX = null;
      this.endY = null;
      // Reset the drawing first line flag
      this.isDrawingFirstLine = false;
      this.hasPendingStartPoint = false;
      this.redrawAll();
      this.wall.style.cursor = "cell";
    }
  }

  // drawAllLines()
  drawAllLines() {
    // Draw existing walls first (if any) with 60% opacity
    if (this.wallsValue && this.wallsValue.length > 0) {
      this.wallsValue.forEach((wall) => {
        // In edit mode, draw the original wall being edited only if no new lines are drawn and not currently drawing
        if (
          this.isEditMode &&
          this.originalWallData &&
          wall.id.toString() === this.originalWallData.id
        ) {
          if (this.lines.length === 0 && !this.isDrawingFirstLine) {
            // No new lines drawn yet and not currently drawing first line, show original wall with current selected color
            const activeColor = this.color || wall.color;
            this.drawLine(
              wall.startX,
              wall.startY,
              wall.endX,
              wall.endY,
              activeColor,
              1.0,
            );
          }
          // If new lines exist or currently drawing first line, skip drawing the original wall
          return;
        }
        this.drawLine(
          wall.startX,
          wall.startY,
          wall.endX,
          wall.endY,
          wall.color,
          0.6,
        );
      });
    }

    // Draw new lines being drawn with full opacity
    this.lines.forEach((line) => {
      this.drawLine(
        line.startX,
        line.startY,
        line.endX,
        line.endY,
        line.color,
        1.0,
      );
    });
  }


  drawLine(startX, startY, endX, endY, color, opacity = 1.0) {
    this.ctx.beginPath();
    this.ctx.moveTo(startX, startY);
    this.ctx.lineTo(endX, endY);
    this.ctx.strokeStyle = color || "#000000";
    this.ctx.lineWidth = 5;
    this.ctx.globalAlpha = opacity;
    this.ctx.stroke();
    this.ctx.globalAlpha = 1.0; // Reset to full opacity for subsequent operations
  }

  changeMaterial(event) {
    const color = event.currentTarget.dataset.color;
    const thickness = event.currentTarget.dataset.thickness;
    this.colorFormTarget.value = color;
    this.thicknessInputTarget.value = thickness;
    this.color = color;
    // 更新目前線段 
    this.lines.forEach(line => line.color = color);
    // 若正在編輯舊牆，也更新顏色 
    if (this.isEditMode && this.originalWallData) {
      const target = this.wallsValue.find(w => w.id.toString() === this.originalWallData.id);
      if (target) target.color = color;
    }
    this.notifyFormChanged();
    console.log("[CanvasController] changeMaterial after update lines:",
      this.lines.map(l => l.color));
    this.redrawAll();
  }

  setFormFields(lines) {
    document.getElementById("wall_lines").value = lines;

    // Trigger change detection for the confirm controller
    this.notifyFormChanged();
  }

  notifyFormChanged() {
    // Dispatch a custom event to notify that the form has changed
    const changeEvent = new CustomEvent("formChanged", {
      detail: { source: "canvas" },
      bubbles: true,
    });
    document.dispatchEvent(changeEvent);
  }

  clearLines() {
    this.lines = [];
    // Reset the drawing first line flag
    this.isDrawingFirstLine = false;
    this.setFormFields(JSON.stringify(this.lines));
    this.updateLinesInfo();
    this.redrawAll();

    // In edit mode, show the warning again when lines are cleared
    if (this.isEditMode) {
      this.showEditModeWarning();
      this.showToast(
        "All new wall lines cleared. The original wall is now visible again.",
        "info",
      );
    } else {
      this.showToast(
        "All wall lines cleared. You can start drawing new lines.",
        "info",
      );
    }

    // Also notify that the form has changed (even though we cleared lines, it's still a change)
    this.notifyFormChanged();
  }

  getLinesCount() {
    return this.lines.length;
  }

  getTotalLength() {
    return this.lines.reduce((total, line) => {
      const dx = line.endX - line.startX;
      const dy = line.endY - line.startY;
      return total + Math.sqrt(dx * dx + dy * dy);
    }, 0);
  }

  updateLinesInfo() {
    this.updateLinesCount();
    this.updateWarningMessage();
    this.showLineCountFeedback();
  }

  showLineCountFeedback() {
    const linesCount = this.lines.length;
    if (this.isEditMode) {
      if (linesCount === 1) {
        this.showToast(
          "1 new wall line drawn. This will replace the original wall. You can draw more lines or save now.",
        );
      } else if (linesCount > 1) {
        this.showToast(
          `${linesCount} new wall lines drawn. This will replace the original wall. All lines will use the same material and thickness.`,
        );
      }
    } else {
      if (linesCount === 1) {
        this.showToast(
          "1 wall line drawn. You can draw more lines or save now.",
        );
      } else if (linesCount > 1) {
        this.showToast(
          `${linesCount} wall lines drawn. All lines will use the same material and thickness.`,
        );
      }
    }
  }

  showToast(message, type = "success") {
    // Simple toast notification
    const toast = document.createElement("div");
    toast.className = "toast-notification";
    toast.textContent = message;

    // Set styling based on type
    const styles = {
      success: {
        background: "#64be00",
        color: "white",
      },
      warning: {
        background: "#f8a600",
        color: "white",
      },
      error: {
        background: "#ff2837",
        color: "white",
      },
    };

    const style = styles[type] || styles.success;

    toast.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      background: ${style.background};
      color: ${style.color};
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 1000;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    `;

    document.body.appendChild(toast);

    document.body.dataset.lastToastMessage = message;
    document.body.dataset.lastToastType = type;
    document.body.dataset.lastToastAt = String(Date.now());

    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = "0";
      toast.style.transition = "opacity 0.5s";
      setTimeout(() => document.body.removeChild(toast), 500);
    }, 3000);
  }

  onCanvasClick(event) {
    if (this.isShow || !this.hasPopupTarget) {
      return;
    }
    const x = event.offsetX;
    const y = event.offsetY;

    const clickedWall = this.wallsValue.find((wall) =>
      this.isNearLine(x, y, wall),
    );

    if (clickedWall) {
      this.isShow = true;
      this.startX = clickedWall.startX;
      this.startY = clickedWall.startY;
      this.endX = clickedWall.endX;
      this.endY = clickedWall.endY;
      this.color = clickedWall.color;
      this.drawLine(
        this.startX,
        this.startY,
        this.endX,
        this.endY,
        "#3c9f00",
        1.0,
      );
      this.showWallInfo(clickedWall);
    }
  }

  drawDot(startX, startY, endX, endY) {
    const percent = 0.03;
    const dx = endX - startX;
    const dy = endY - startY;
    const headX = startX + dx * percent;
    const headY = startY + dy * percent;
    this.drawLine(startX, startY, headX, headY, "#3c9f00", 1.0);

    const tailX = startX + dx * (1 - percent);
    const tailY = startY + dy * (1 - percent);

    this.drawLine(tailX, tailY, endX, endY, "#3c9f00", 1.0);
  }

  isNearLine(x, y, wall) {
    const distance = this.pointToSegmentDistance(
      x,
      y,
      wall.startX,
      wall.startY,
      wall.endX,
      wall.endY,
    );
    return distance <= 10;
  }

  pointToSegmentDistance(px, py, x1, y1, x2, y2) {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = dot / lenSq;

    if (param < 0) param = 0;
    else if (param > 1) param = 1;

    const xx = x1 + param * C;
    const yy = y1 + param * D;

    const dx = px - xx;
    const dy = py - yy;

    return Math.sqrt(dx * dx + dy * dy);
  }

  showWallInfo(wall) {
    if (this.hasPopupTarget) {
      const popup = this.popupTarget;
      const content = popup.querySelector(".popup-content");

      content.innerHTML = `
        <span id="popup-material" class="text-f1">
          Material: <span class="text-p5">${wall.material}</span>
        </span>
        <span id="popup-thickness" class="text-f1">
          Thickness (${wall.unit}): <span class="text-p5">${wall.thickness}</span>
        </span>
      `;

      const currentPath = window.location.pathname;
      this.editLinkTarget.href = `${currentPath}/${wall.id}/edit`;
      this.deleteLinkTarget.href = `${currentPath}/${wall.id}`;
      this.deleteLinkTarget.dataset.url = `${currentPath}/${wall.id}`;

      popup.classList.remove("hidden");
    }
  }

  redraw() {
    const minX = Math.min(this.startX, this.endX);
    const minY = Math.min(this.startY, this.endY);
    const width = Math.abs(this.endX - this.startX);
    const height = Math.abs(this.endY - this.startY);

    this.ctx.clearRect(minX, minY, width, height);
    this.drawLine(
      this.startX,
      this.startY,
      this.endX,
      this.endY,
      this.color,
      1.0,
    );
  }

  redrawAll() {
    this.ctx.clearRect(0, 0, this.wall.width, this.wall.height);
    this.drawAllLines();
  }

  hidePopup() {
    if (this.hasPopupTarget) {
      this.isShow = false;
      this.drawLine(
        this.startX,
        this.startY,
        this.endX,
        this.endY,
        this.color,
        1.0,
      );
      this.popupTarget.classList.add("hidden");
    }
  }

  showWarnDrawPopup() {
    if (this.hasWarnDrawPopupTarget) {
      this.warnDrawPopupTarget.classList.remove("hidden");
    }
  }

  closeWarnDrawPopup() {
    if (this.hasWarnDrawPopupTarget) {
      this.warnDrawPopupTarget.classList.add("hidden");
    }
  }

  updateLinesCount() {
    if (this.hasLinesCountTarget) {
      this.linesCountTarget.textContent = this.lines.length;
    }
    this.updateWarningMessage();
  }

  updateWarningMessage() {
    const warningElement = document.getElementById("material_change_warning");
    if (warningElement) {
      if (this.isEditMode) {
        // In edit mode, show warning about replacing the original wall
        if (this.lines.length > 0) {
          warningElement.style.display = "block";
          warningElement.innerHTML = `
            <span class="text-f1 warning-text">
              ⚠️ <strong>Edit Mode:</strong> New wall lines will replace the original wall. 
              Changing <strong>Material</strong> or <strong>Thickness</strong> will affect all new lines.
            </span>
          `;
        } else {
          warningElement.style.display = "none";
        }
      } else {
        // In new mode, show the original warning
        if (this.lines.length > 1) {
          warningElement.style.display = "block";
          warningElement.innerHTML = `
            <span class="text-f1 warning-text">
              ⚠️ Changing <strong>Material</strong> or <strong>Thickness</strong> will affect all drawn lines
            </span>
          `;
        } else {
          warningElement.style.display = "none";
        }
      }
    }
  }

  validateFormSubmission(event) {
    if (this.lines.length === 0) {
      if (this.isEditMode) {
        // In edit mode, allow saving without drawing new lines (pure property editing)
        return true;
      } else {
        // In new mode, require at least one line
        event.preventDefault();
        alert("Please draw at least one wall line before saving.");
        return false;
      }
    }

    // Validate that all lines have proper coordinates
    const invalidLines = this.lines.filter(
      (line) =>
        !line.startX ||
        !line.startY ||
        !line.endX ||
        !line.endY ||
        isNaN(line.startX) ||
        isNaN(line.startY) ||
        isNaN(line.endX) ||
        isNaN(line.endY),
    );

    if (invalidLines.length > 0) {
      event.preventDefault();
      alert(
        "Some wall lines have invalid coordinates. Please redraw the invalid lines.",
      );
      return false;
    }

    // Form is valid, reset the change detection
    this.resetFormChanged();

    return true;
  }

  resetFormChanged() {
    // Dispatch a custom event to reset the form change detection
    const resetEvent = new CustomEvent("formReset", {
      detail: { source: "canvas" },
      bubbles: true,
    });
    document.dispatchEvent(resetEvent);
  }

  hasUnsavedChanges() {
    return this.lines.length > 0;
  }

  // This method can be called to check if the user has made changes
  checkForChanges() {
    if (this.hasUnsavedChanges()) {
      this.notifyFormChanged();
    }
  }

  handleKeyDown(event) {
    if (event.key === "Delete" && this.mode === "browse" && this.selectedWall) {
      this.deleteSelectedWall();
    }
    if (event.key === "Escape") {
      this.isDrawing = false;
      this.startX = this.startY = this.endX = this.endY = null;
      this.hasPendingStartPoint = false;
      this.redrawAll();
      this.updateCursor("default");
    }
  }

  handleBeforeUnload() {
    if (this.hasUnsavedChanges()) {
      return "You have unsaved changes. Are you sure you want to leave this page?";
    }
  }

  clearOriginalWallFromCanvas() {
    // Clear the original wall from the canvas by redrawing all walls except the original one
    this.redrawCanvasWithoutOriginalWall();
  }

  redrawCanvasWithoutOriginalWall() {
    // Clear the entire canvas
    this.ctx.clearRect(0, 0, this.wall.width, this.wall.height);

    // Redraw all walls except the original one being edited
    if (this.wallsValue && this.wallsValue.length > 0) {
      this.wallsValue.forEach((wall) => {
        // Skip the wall that's being edited
        if (wall.id.toString() !== this.originalWallData.id) {
          this.drawLine(
            wall.startX,
            wall.startY,
            wall.endX,
            wall.endY,
            wall.color,
            0.6,
          );
        }
      });
    }

    // Draw any new lines that might have been drawn
    this.lines.forEach((line) => {
      this.drawLine(
        line.startX,
        line.startY,
        line.endX,
        line.endY,
        line.color,
        1.0,
      );
    });
  }

  toggle(event) {
    const isEnable = event.params.enabled;
    this.enabled = isEnable;
    if (isEnable) {
      this.wallTarget.style.cursor = "cell";
    } else {
      this.wallTarget.style.cursor = "unset";
    }
  }
}

// startDraw(e) {
//   if (!this.enabled) return;
//   this.closeWarnDrawPopup();

//   // In edit mode, mark that we're starting to draw (this will hide original wall)
//   if (this.isEditMode && this.lines.length === 0) {
//     this.isDrawingFirstLine = true;
//   }

//   const rect = this.wall.getBoundingClientRect();
//   this.scale = parseFloat(this.wallScaleTarget.dataset.scale) || 1;
//   this.startX = (e.clientX - rect.left) / this.scale;
//   this.startY = (e.clientY - rect.top) / this.scale;
//   this.isDrawing = true;

//   // Change cursor to indicate drawing mode
//   this.wall.style.cursor = "crosshair";

//   // Show a small indicator at the start point
//   this.drawStartPoint(this.startX, this.startY);
// }

// draw(e) {
//   if (!this.enabled) return;
//   if (!this.isDrawing) return;
//   const rect = this.wall.getBoundingClientRect();
//   const currentX = (e.clientX - rect.left) / this.scale;
//   const currentY = (e.clientY - rect.top) / this.scale;

//   // Clear the canvas and redraw all existing lines
//   this.redrawAll();

//   // Draw the preview line
//   this.ctx.beginPath();
//   this.ctx.moveTo(this.startX, this.startY);
//   this.ctx.lineTo(currentX, currentY);
//   this.ctx.strokeStyle = this.color;
//   this.ctx.lineWidth = 5;
//   this.ctx.stroke();

//   // Add a subtle dashed effect for the preview
//   this.ctx.setLineDash([5, 5]);
//   this.ctx.stroke();
//   this.ctx.setLineDash([]); // Reset to solid line
// }

// endDraw(e) {
//   if (!this.isDrawing) return;
//   this.isDrawing = false;
//   const rect = this.wall.getBoundingClientRect();

//   this.endX = (e.clientX - rect.left) / this.scale;
//   this.endY = (e.clientY - rect.top) / this.scale;

//   // Only create a line if there's actual movement (dragging)
//   const minDistance = 10; // Minimum distance in pixels to consider it a valid line
//   const distance = Math.sqrt(
//     Math.pow(this.endX - this.startX, 2) +
//       Math.pow(this.endY - this.startY, 2),
//   );

//   if (distance >= minDistance) {
//     // Ensure all lines use the same color (current material color)
//     const lineColor = this.color;

//     this.lines.push({
//       startX: this.startX,
//       startY: this.startY,
//       endX: this.endX,
//       endY: this.endY,
//       color: lineColor,
//     });

//     // Hide edit mode warning when first line is drawn and reset drawing flag
//     if (this.isEditMode && this.lines.length === 1) {
//       this.hideEditModeWarning();
//       this.isDrawingFirstLine = false;
//     }

//     this.setFormFields(JSON.stringify(this.lines));
//     this.updateLinesInfo();
//   } else {
//     // Reset drawing state if no valid line was created
//     this.startX = null;
//     this.startY = null;
//     this.endX = null;
//     this.endY = null;
//     // Reset the drawing first line flag since no line was created
//     this.isDrawingFirstLine = false;

//     // Show feedback for invalid line
//     this.showToast(
//       "Wall line is too short. Please drag to create a longer line.",
//       "warning",
//     );
//   }

//   // Reset cursor back to normal
//   this.wall.style.cursor = "cell";
// }
